/**
 * LoginModal Component
 *
 * The connect dialog for a broker account: it explains the handoff, then hands
 * off. On a phone it is a DS Sheet that rises from the bottom (grabber +
 * drag-to-dismiss); at tablet and above it is a centred DS Modal. Same body in
 * both — this mirrors the auth screen (LoginOverlay), which likewise slides up on
 * a phone and settles centred on a desktop.
 *
 * PRESENTATION ONLY. Every piece of state, every effect, every handler and both
 * payload mappings below are byte-for-byte what they were; the login mechanism
 * (BrokerLoginStep's URL fetch, window.open, and the storage/event listeners that
 * report the result) is untouched. What changed is what the user reads and sees.
 *
 * What it used to say, on one small surface:
 *   · "Your session with X has expired or will expire soon" — hardcoded to the
 *     isReconnect prop, so it asserted expiry even with the card behind it reading
 *     "Session Active · 12h 43m". It now reads the ONE shared mapping
 *     (./accountStatus) that the card itself reads, so the two cannot disagree.
 *   · the broker's name FIVE times (title, alert, "Account Details", the inner
 *     broker card's heading, and the button)
 *   · "Account:" followed by nothing — accountData.account_name is not a field the
 *     API returns
 *   · "Click the button below to login to your broker platform", directly above
 *     that button
 *   · the account's raw UUID
 *   · three ways out: X, Back, Cancel
 *   · two MUI <Alert>s and three nested boxes
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Modal, Sheet, Icon } from "../../../components/ds";
import useBreakpoint from "../../../hooks/useBreakpoint";
import { useAuth } from "../../../contexts/AuthContext";
import { sessionStatusOf } from "../accountStatus";
import AlertMessage from "./AlertMessage";
import BrokerLoginStep from "../AddOrEditTradeAccount/Steps/BrokerLoginStep";

const LoginModal = ({
  open,
  onClose,
  account,
  onLoginSuccess,
  isReconnect = true,
  title,
}) => {
  const { isPhone } = useBreakpoint();
  const [isLoading, setIsLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!open) {
      setIsLoading(false);
      setShowSnackbar(false);
      setSnackbarMessage("");
      setSnackbarSeverity("info");
    }
  }, [open]);

  // The app's live broker-account state, kept current by the broker-session WS topic.
  const { brokerSessions } = useAuth();

  // One guard so the WS-primary path and the localStorage fallback can't BOTH run the
  // success animation. Reset each time the dialog opens.
  const successHandledRef = useRef(false);
  const baselineExpiryRef = useRef(0);
  useEffect(() => {
    if (open) successHandledRef.current = false;
  }, [open]);

  // The single success path. WS-primary and poll-fallback both land here; the guard
  // makes the second caller a no-op.
  const handleConnected = useCallback(
    (brokerName) => {
      if (successHandledRef.current) return;
      successHandledRef.current = true;
      setSnackbarSeverity("success");
      setSnackbarMessage(
        `Connected to ${
          brokerName ||
          account?.broker_display_name ||
          account?.broker_name ||
          "your broker"
        }`
      );
      setShowSnackbar(true);
      localStorage.removeItem("brokerSessionStatus");
      setTimeout(() => {
        onClose();
        if (onLoginSuccess) {
          onLoginSuccess({ success: true, brokerAccountId: account?.account_id, brokerName });
        }
      }, 1200);
    },
    [account, onClose, onLoginSuccess]
  );

  // WS PRIMARY. The backend broadcasts session_created on broker-session.{userId};
  // AuthContext receives it IN THIS TAB — tab-agnostically, so it does not matter that
  // the broker login and callback ran in a different tab — and advances this account's
  // session_expires_at in brokerSessions. We snapshot the expiry when the dialog opens,
  // then treat any advance past that baseline as "the connection landed". This is what
  // lets the flow stop depending on two tabs signalling each other.
  useEffect(() => {
    if (!open || !account?.account_id) return;
    const mine = brokerSessions.find(
      (s) => s.account_id === account.account_id || s.id === account.account_id
    );
    baselineExpiryRef.current = mine?.session_expires_at
      ? new Date(mine.session_expires_at).getTime()
      : 0;
    // Snapshot once per open; the watcher below reacts to later WS-driven changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account?.account_id]);

  useEffect(() => {
    if (!open || !account?.account_id || successHandledRef.current) return;
    const mine = brokerSessions.find(
      (s) => s.account_id === account.account_id || s.id === account.account_id
    );
    const exp = mine?.session_expires_at
      ? new Date(mine.session_expires_at).getTime()
      : 0;
    // A fresh session pushes expiry later than the baseline captured at open.
    if (exp > baselineExpiryRef.current && exp > Date.now()) {
      handleConnected(account?.broker_display_name || account?.broker_name);
    }
  }, [brokerSessions, open, account, handleConnected]);

  // FALLBACK — the refresh mechanism we agreed to keep BEHIND the WS. The callback tab
  // writes the result to localStorage (brokerSessionStatus), which IS shared across
  // tabs, and this polls it every 2s; if the WS above already handled success the guard
  // makes it a no-op. The old window CustomEvent listener was removed: it was dispatched
  // in the callback tab and a CustomEvent cannot cross tabs, so it never fired here.
  useEffect(() => {
    if (!open || !account?.account_id) return;

    const checkBrokerSessionStatus = () => {
      try {
        const storedStatus = localStorage.getItem("brokerSessionStatus");
        if (!storedStatus) return;
        const status = JSON.parse(storedStatus);

        const isRecent = Date.now() - status.timestamp < 5 * 60 * 1000;
        const isForCurrentAccount =
          status.brokerAccountId === account.account_id ||
          status.broker_account_id === account.account_id;
        if (!isRecent || !isForCurrentAccount) return;

        if (status.success) {
          handleConnected(status.brokerName);
        } else if (status.error) {
          setSnackbarSeverity("error");
          setSnackbarMessage(status.error || "Connection failed");
          setShowSnackbar(true);
          localStorage.removeItem("brokerSessionStatus");
        }
      } catch (err) {
        console.error("Error checking broker session status:", err);
      }
    };

    checkBrokerSessionStatus();
    const statusCheckInterval = setInterval(checkBrokerSessionStatus, 2000);
    return () => clearInterval(statusCheckInterval);
  }, [open, account, handleConnected]);

  // Handle successful login reported IN-PAGE by BrokerLoginStep (the same-tab path,
  // rare — the broker login almost always completes in the other tab and lands via the
  // WS/poll above). Routed through the same guarded handler so it can't double-fire.
  const handleLoginSuccess = () => {
    handleConnected(account?.broker_display_name || account?.broker_name);
  };

  // Handle modal close
  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  // Handle back button in login module
  const handleBack = () => {
    handleClose();
  };

  // THESE THREE MUST KEEP THEIR IDENTITY ACROSS RENDERS.
  //
  // BrokerLoginStep fetches the login URL from an effect keyed on object identity:
  //
  //     initializeLogin = useCallback(…, [accountData, isEditMode, onStatusChange])
  //     useEffect(() => { … initializeLogin() }, [accountData, initializeLogin])
  //
  // Both payloads below used to be rebuilt inline on every render, and
  // onStatusChange was an inline arrow — so every render of this component minted
  // three new identities, which made `initializeLogin` new, which re-fired the
  // effect, which fetched the login URL again. And this component re-renders
  // whenever AuthContext's value changes (a broker-session refetch, the WS landing
  // an update, the refreshing flag toggling) — none of which has anything to do
  // with the login URL.
  //
  // The user sees that as the primary button flickering: its contents swap to the
  // loader and back, repeatedly, instead of settling. Measured on the desktop path
  // before this change — ONE dialog open produced:
  //     Preparing… → Continue to ICICI Direct → Preparing… → Continue to ICICI Direct
  //
  // Keyed on account_id rather than on `account`: the object is REPLACED wholesale
  // by every accounts refetch, so keying on it would re-fire the fetch every time
  // the list reloads. Nothing read below changes for a given account_id — the
  // broker and its names are fixed per account — and the two volatile fields
  // (status/session_status) are not read by the fetch or by the compact dialog.
  const accountId = account?.account_id || account?.id;

  // Prepare account data for BrokerLoginStep
  const accountDataForLogin = useMemo(
    () =>
      account
        ? {
            account_id: account.account_id || account.id,
            account_name: account.account_name || account.name,
            account_holder_name: account.account_holder_name,
            broker_id: account.broker_id,
            broker_name: account.broker_name,
            broker_display_name: account.broker_display_name,
            status: account.status,
            session_status: account.session_status,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId]
  );

  // Prepare selected broker object for BrokerLoginStep
  const selectedBrokerForLogin = useMemo(
    () =>
      account
        ? {
            id: account.broker_id,
            broker_id: account.broker_id,
            name: account.broker_name || account.name,
            display_name:
              account.broker_display_name ||
              account.display_name ||
              account.broker_name,
            code: account.broker_code || account.code,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId]
  );

  // Stable identity, for the same reason: this feeds initializeLogin's deps.
  const handleStatusChange = useCallback((status, instructions, params) => {
    // Handle status changes from BrokerLoginStep if needed
    console.log("LoginModal - Login status change:", status, instructions, params);
  }, []);

  if (!account) {
    return null;
  }

  const brokerName =
    account.broker_display_name || account.broker_name || "your broker";

  // The SAME reading the account card uses, so the dialog cannot contradict the
  // card that opened it.
  const session = sessionStatusOf(account);

  // Named for what is actually about to happen, from real state — not from the
  // isReconnect prop, which knows nothing about the session.
  const heading =
    title || (session.isActive ? `Renew ${brokerName} session` : `Connect ${brokerName}`);

  const body = (
    <div className="flex flex-col gap-5">
      {/* On a phone the heading is rendered HERE rather than passed to Sheet's
          `title`. Sheet only draws its header when it has a title, and that header
          carries a "Done" button gated on `dismissible` — but `dismissible` also
          gates BACKDROP CLICK (Sheet.jsx:222) and DRAG-TO-DISMISS (Sheet.jsx:109).
          Passing dismissible={false} to lose one wrong WORD silently removed two
          real ways out. Owning the heading keeps dismissible at its default, so
          tap-outside and drag both work. (The DS conflating "can be dismissed"
          with "shows a Done button" is a real API flaw — worth separating, but not
          from inside this file.) */}
      {isPhone && <span className="ds-sheet-title">{heading}</span>}

      {/* Who, and where that account stands right now. */}
      <div className="flex items-center gap-3">
        <div className="bg-bg-3 text-fg-2 rounded-lg p-2 shrink-0">
          <Icon name="bank" size={18} />
        </div>
        <div className="flex flex-col min-w-0 gap-1">
          <span className="text-sm font-semibold text-fg-1 truncate">
            {brokerName}
          </span>
          <span className={`ds-badge sm ${session.variant} self-start`}>
            {session.label}
          </span>
        </div>
      </div>

      {/* What happens next, in one sentence — and the one reassurance that is
          worth the space on a screen that is about to ask for a broker password
          (it is asked for on the broker's own site, never here). */}
      <div className="flex flex-col gap-1">
        <p className="ds-body text-fg-2" style={{ margin: 0 }}>
          You&rsquo;ll sign in on {brokerName}&rsquo;s own site and come straight
          back here.
        </p>
        <p className="text-xs text-fg-3" style={{ margin: 0 }}>
          Your {brokerName} password is never shared with DeeTrade.
        </p>
      </div>

      {showSnackbar && (
        <AlertMessage
          severity={snackbarSeverity}
          message={snackbarMessage}
          onClose={() => setShowSnackbar(false)}
        />
      )}

      {/* The action + its live states. `compact` strips the wizard chrome this
          component draws for the stepper; the mechanism is identical. */}
      {accountDataForLogin && selectedBrokerForLogin && (
        <BrokerLoginStep
          compact
          accountData={accountDataForLogin}
          selectedBroker={selectedBrokerForLogin}
          isEditMode={isReconnect}
          onLoginSuccess={handleLoginSuccess}
          onBack={handleBack}
          loading={isLoading}
          onStatusChange={handleStatusChange}
        />
      )}

      <button
        type="button"
        onClick={handleClose}
        disabled={isLoading}
        className="btn btn-ghost w-full"
      >
        Cancel
      </button>
    </div>
  );

  // Phone → bottom sheet (grabber, drag-to-dismiss, tap-outside, spring).
  // Tablet/desktop → centred dialog. No `title` and no `dismissible` override:
  // both defaults are what we want, and the heading is drawn in `body` (see the
  // note there for why touching either would cost real dismiss affordances).
  if (isPhone) {
    return (
      <Sheet open={open} onClose={handleClose}>
        {body}
      </Sheet>
    );
  }

  return (
    <Modal open={open} onClose={handleClose} size="sm" title={heading}>
      {body}
    </Modal>
  );
};

export default LoginModal;
